import {
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
  type AuthenticationResult,
} from '@azure/msal-browser';

const TENANT_ID = import.meta.env.VITE_AAD_TENANT_ID as string;
const CLIENT_ID = import.meta.env.VITE_AAD_CLIENT_ID as string;
const API_SCOPE = import.meta.env.VITE_API_SCOPE as string;

const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
  system: {
    // Silent renewal happens in a hidden iframe; the default 10s gives up on
    // slow corporate networks and surfaces "timed_out". Be more patient.
    iframeBridgeTimeout: 20000,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

let bootstrapped: Promise<AccountInfo | null> | null = null;

/**
 * One-shot bootstrap. Initialises MSAL, handles any redirect return,
 * and returns the active account (or null if signed out).
 * Safe to call multiple times — subsequent calls await the same promise.
 */
export function bootstrapAuth(): Promise<AccountInfo | null> {
  if (bootstrapped) return bootstrapped;
  bootstrapped = (async () => {
    await msalInstance.initialize();
    const redirectResult = await msalInstance.handleRedirectPromise();
    if (redirectResult?.account) {
      msalInstance.setActiveAccount(redirectResult.account);
      return redirectResult.account;
    }
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
      return accounts[0];
    }
    return null;
  })();
  return bootstrapped;
}

export function signIn(): Promise<void> {
  // Return to the exact page after sign-in so deep links (e.g. ?tab=monitor
  // from the Friday email) survive the redirect.
  return msalInstance.loginRedirect({ scopes: [API_SCOPE], redirectStartPage: window.location.href });
}

export function signOut(): Promise<void> {
  return msalInstance.logoutRedirect();
}

export async function getAccessToken(): Promise<string | null> {
  const account = msalInstance.getActiveAccount();
  if (!account) return null;
  const result: AuthenticationResult = await msalInstance.acquireTokenSilent({
    scopes: [API_SCOPE],
    account,
  });
  return result.accessToken;
}

export function getActiveAccount(): AccountInfo | null {
  return msalInstance.getActiveAccount();
}